/**
 * Lockstep Platform SDK for TypeScript
 *
 * (c) 2021-2023 Lockstep, Inc.
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 *
 * @author     Lockstep Network <support@lockstep.io>
 * @copyright  2021-2023 Lockstep, Inc.
 * @link       https://github.com/Lockstep-Network/lockstep-sdk-typescript
 */
export class UserAccountsClient {
    /**
     * Internal constructor for this client library
     */
    constructor(client) {
        this.client = client;
    }
    /**
     * Retrieves the User with this identifier.
     *
     * A User represents a person who has the ability to authenticate against the Lockstep Platform and use services such as Lockstep Inbox.  A User is uniquely identified by an Azure identity, and each user must have an email address defined within their account.  All Users must validate their email to make use of Lockstep platform services.  Users may have different privileges and access control rights within the Lockstep Platform.
     *
     * @param id The unique ID number of the User to retrieve
     * @param include To fetch additional data on this object, specify the list of elements to retrieve. Available collections: Notes, Attachments, CustomFields, AccountingRole
     */
    retrieveUser(id, include) {
        const url = `/api/v1/UserAccounts/${id}`;
        const options = {
            params: {
                include,
            },
        };
        return this.client.request("get", url, options, null);
    }
    /**
     * Updates a User that matches the specified id with the requested information.
     *
     * The PATCH method allows you to change specific values on the object while leaving other values alone.  As input you should supply a list of field names and new values.  If you do not provide the name of a field, that field will remain unchanged.  This allows you to ensure that you are only updating the specific fields desired.
     *
     * A User represents a person who has the ability to authenticate against the Lockstep Platform and use services such as Lockstep Inbox.  A User is uniquely identified by an Azure identity, and each user must have an email address defined within their account.  All Users must validate their email to make use of Lockstep platform services.  Users may have different privileges and access control rights within the Lockstep Platform.
     *
     * @param id The unique ID number of the User to retrieve
     * @param body A list of changes to apply to this User
     */
    updateUser(id, body) {
        const url = `/api/v1/UserAccounts/${id}`;
        return this.client.request("patch", url, null, body);
    }
    /**
     * Disable the user referred to by this unique identifier.
     *
     * A User represents a person who has the ability to authenticate against the Lockstep Platform and use services such as Lockstep Inbox.  A User is uniquely identified by an Azure identity, and each user must have an email address defined within their account.  All Users must validate their email to make use of Lockstep platform services.  Users may have different privileges and access control rights within the Lockstep Platform.
     *
     * @param id The unique Lockstep Platform ID number of this User
     */
    disableUser(id) {
        const url = `/api/v1/UserAccounts/${id}`;
        return this.client.request("delete", url, null, null);
    }
    /**
     * Invite a user with the specified email to join your accounting group. The user will receive an email to set up their account.
     *
     * A User represents a person who has the ability to authenticate against the Lockstep Platform and use services such as Lockstep Inbox.  A User is uniquely identified by an Azure identity, and each user must have an email address defined within their account.  All Users must validate their email to make use of Lockstep platform services.  Users may have different privileges and access control rights within the Lockstep Platform.
     *
     * @param body The user to invite
     */
    inviteUser(body) {
        const url = `/api/v1/UserAccounts/invite`;
        return this.client.request("post", url, null, body);
    }
    /**
     * Retrieves invite information for the specified invite token.
     *
     * A User represents a person who has the ability to authenticate against the Lockstep Platform and use services such as Lockstep Inbox.  A User is uniquely identified by an Azure identity, and each user must have an email address defined within their account.  All Users must validate their email to make use of Lockstep platform services.  Users may have different privileges and access control rights within the Lockstep Platform.
     *
     * @param code The code of the invite
     */
    retrieveInviteData(code) {
        const url = `/api/v1/UserAccounts/invite`;
        const options = {
            params: {
                code,
            },
        };
        return this.client.request("get", url, options, null);
    }
    /**
     * Transfer the ownership of a group to another user. This API must be called by the current owner of the group.
     *
     * A User represents a person who has the ability to authenticate against the Lockstep Platform and use services such as Lockstep Inbox.  A User is uniquely identified by an Azure identity, and each user must have an email address defined within their account.  All Users must validate their email to make use of Lockstep platform services.  Users may have different privileges and access control rights within the Lockstep Platform.
     *
     * @param body Documentation pending
     */
    transferOwner(body) {
        const url = `/api/v1/UserAccounts/transfer-owner`;
        return this.client.request("post", url, null, body);
    }
    /**
     * Queries Users for this account using the specified filtering, sorting, nested fetch, and pagination rules requested. A User represents a person who has the ability to authenticate against the Lockstep Platform and use services such as Lockstep Inbox.  A User is uniquely identified by an Azure identity, and each user must have an email address defined within their account.  All Users must validate their email to make use of Lockstep platform services.  Users may have different privileges and access control rights within the Lockstep Platform.
     *
     * @param filter The filter for this query. See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param include To fetch additional data on this object, specify the list of elements to retrieve. Available collections: Notes, Attachments, CustomFields, AccountingRole
     * @param order The sort order for this query. See See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageSize The page size for results (default 250, maximum of 500). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageNumber The page number for results (default 0). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     */
    queryUsers(filter, include, order, pageSize, pageNumber) {
        const url = `/api/v1/UserAccounts/query`;
        const options = {
            params: {
                filter,
                include,
                order,
                pageSize,
                pageNumber,
            },
        };
        return this.client.request("get", url, options, null);
    }
    /**
     * Change the active GroupKey of the calling user.
     *
     * A User represents a person who has the ability to authenticate against the Lockstep Platform and use services such as Lockstep Inbox.  A User is uniquely identified by an Azure identity, and each user must have an email address defined within their account.  All Users must validate their email to make use of Lockstep platform services.  Users may have different privileges and access control rights within the Lockstep Platform.
     *
     * @param groupKey Documentation pending
     */
    changeUserGroup(groupKey) {
        const url = `/api/v1/UserAccounts/change-group`;
        const options = {
            params: {
                groupKey,
            },
        };
        return this.client.request("post", url, options, null);
    }
    /**
     * Retrieves the user data for the current user. This allows for retrieving extended user data such as UTM parameters.
     *
     * @param include The set of data to retrieve. To avoid any casing confusion, these values are converted to upper case. Possible values are: UTM
     */
    getUserData(include) {
        const url = `/api/v1/UserAccounts/user-data`;
        const options = {
            params: {
                include,
            },
        };
        return this.client.request("get", url, options, null);
    }
    /**
     * Set support access for the calling user.
     *
     * Support access allows Lockstep to access the user's account to troubleshoot issues. Access is granted for a limited time, can be revoked at any time, and requires a code to verify the access.
     *
     * Every call to this API will regenerate the support access code.
     *
     * @param body Documentation pending
     */
    setSupportAccess(body) {
        const url = `/api/v1/UserAccounts/support-access`;
        return this.client.request("post", url, null, body);
    }
}
//# sourceMappingURL=UserAccountsClient.js.map